import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'design-system/styles';
import {
  TextCollectionParams,
  TextCollectionFormAction,
  TextCollectionFormParams,
  TextCollectionField,
  TextCollectionFieldType,
  TextCollectionAttachment,
  TextCollectionAttachmentType,
  TextCollectionRankingOrderOption,
  FormField as FormFieldType,
  LogBehaviourActions,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionResponse,
  TextCollectionAnnotation,
  BoundingBox,
} from '@scale/shared/types/response/textcollection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import TextCollectionForm from './TextCollectionForm';
import {
  Breadcrumb,
  getUpdateNextAnnotations,
  getDeleteNextAnnotations,
  getCurrentAnnotations,
} from './breadcrumbs';
import { getAutoCompleteFieldSetResponses, AutoCompleteQuery } from './autoComplete';
import AnnotationTree from './AnnotationTree';
import { FieldSetReview, getUpdateNextReviews } from './review';
import { saveBackup, getBackup } from './localStorage';
import AttachmentCarousel from './AttachmentCarousel';
import { useTextCollection } from './reducer';
import ReviewFooter from './ReviewFooter';
import { DocumentProvider } from './document';
import { SuggestionsProvider } from './suggestions';
import TextCollectionModalForm from './TextCollectionModalForm';
import { saveServerCheckpoint } from './serverCheckpoint';
import { isFormFields } from '@scale/shared/textcollection/field';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { LintDiagnostic } from '@scale/shared/task_lint/task_linter';
import { useTextFragments } from '@scale/frontend-lib/ocr';
import useWorkflowHook from '../../Sidebar/GuidedWorkflowPanel/useWorkflow';
import GuidedWorkflowBar from '../../GuidedWorkflowBar';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import classNames from 'classnames';
// eslint-disable-next-line restrict-imports/restrict-imports
import { useMediaQuery } from '@material-ui/core';
import { isAttachmentRelevant, flattenFields } from '@scale/shared/textcollection/helpers';
import { ConfigJson } from '@scale/shared/defaults';
import seedrandom from 'seedrandom';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatbotRankingProvider } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import { useRLHFHackStore } from 'react-scale/components/scalers/tasks/textcollection/stores/rlhfHackStore';
import { LLMHelperProvider } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';

const QUICK_ADD_FIELD_SETS = ['item_extra', 'extra_option', 'item_price'];
const KEYWORDS = ['or', 'choice'];

interface TextCollectionCoreProps {
  agreementScores?: any;
  localBackupKey?: string;
  assignmentId?: string;
  assignment?: AssignmentJson;
  readOnly?: boolean;
  reviewMode?: boolean;
  startInAttemptMode?: boolean;
  params: TextCollectionParams;
  decisionTreeFields?: TextCollectionField[];
  response: TextCollectionResponse;
  loggedUserId?: string;
  useProtectedText?: boolean;
  setResponse: (response: TextCollectionResponse) => void;
  forms?: TextCollectionFormParams[];
  openForms?: Record<string, boolean>;
  requestShowForm?: (formId: string) => void;
  requestCloseForm?: (formId: string) => void;
  requestSubmit?: (formId: string) => void;
  lintErrors?: LintDiagnostic[];
  ocrTextUrls?: string[];
  project?: string;
  showSidebarResponsive?: boolean;
  inlineAttachments?: TextCollectionAttachment[];
  captcha?: string;
  captchaUserAnswer?: string;
  setCaptchaUserAnswer?: (x: string) => void;
  onFormFieldNavigate?: (finished: boolean) => void;
  hideAttachments?: boolean;
  hideTextCollectionForm?: boolean;
  isScaleStudio?: boolean;
  isCustomer?: boolean;

  isAuditMode?: boolean;
  auditId?: string;
  initialResponse?: Partial<TextCollectionResponse>;
  customFunction?: any;
  textCollectionColumnCutoff?: number;

  onSubmitDisabled?: (isDisabled: boolean) => void;

  projectConfig?: Partial<ConfigJson>;
  handleSkip?: (skipReason: string, skipExplanation: string) => void;
  startSelfDestructSequence?: (secs: number) => void;

  isReview?: boolean;

  setCanSubmitRlhf?: (canSubmit: boolean) => void;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  useSimpleCodeField?: boolean;
  setCanSubmitAiAssistant?: (isDone: string[]) => void;
  logBehaviourActions?: LogBehaviourActions;

  taskCustomer?: string;
  perTurnCheck?: () => any;
  isTestQuestion?: boolean;

  fullWidthSidebar?: boolean;

  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  readOnlyMessage?: string;
}

// eslint-disable-next-line complexity
export const TextCollectionCore: React.FC<TextCollectionCoreProps> = props => {
  const concatRlhfAttachments = (a: any) => {
    if (a && a.length > 0) {
      const result = a.map((a: TextCollectionAttachment) => a.content).join('\n');
      return [{ content: result, type: 'text' }] as Array<TextCollectionAttachment>;
    } else {
      return [];
    }
  };

  const isRLHF = !!_.get(props.params, 'chatApiEndpoint');

  const attachments = React.useMemo(
    () =>
      isRLHF ? concatRlhfAttachments(props.params.attachments) : props.params.attachments || [],
    [props.params, isRLHF],
  );
  const mobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD})`);
  const { textFragments } = useTextFragments(props.ocrTextUrls);

  const useSplitScreen = useUnleashFeatureFlag(
    'rapid-split-screen',
    { projectId: props.project || (props.assignment ? props.assignment.projectId : undefined) },
    false,
  );

  const useCrabHack =
    useUnleashFeatureFlag(
      'rapid-crab-3-columns',
      { projectId: props.project || (props.assignment ? props.assignment.projectId : undefined) },
      false,
    ) ||
    props.textCollectionColumnCutoff !== undefined ||
    _.some(props.params.fields, 'show_diff') ||
    false;

  const forceUseRlhfHackStore = !!useUnleashFeatureFlag(
    'force-initialize-rlhf-hack',
    { projectId: props.project || (props.assignment ? props.assignment.projectId : undefined) },
    false,
  );

  // How many fields must be places in left column if useCrabHack === true
  const numLeftColumnFields = _.get(props.params, 'numLeftColumnFields') ?? 1;

  const useRLHFColumn =
    useUnleashFeatureFlag(
      'rapid-chatbot-v2',
      { projectId: props.project || (props.assignment ? props.assignment.projectId : undefined) },
      true,
    ) && isRLHF;

  const useFairStreaming = useUnleashFeatureFlag(
    'rapid-fair-use-streaming',
    { projectId: props.project || (props.assignment ? props.assignment.projectId : undefined) },
    false,
  );

  const sortedFields = React.useMemo(() => {
    const random = seedrandom(props.loggedUserId);
    const fields = (props.params.fields as FormFieldType[]) ?? [];
    const randomFields = fields.filter(field => field.random_order).sort(() => 0.5 - random());
    const staticFields = fields.filter(field => !field.random_order);
    const _sortedFields: TextCollectionField[] = [...randomFields, ...staticFields];
    return _sortedFields.map(field => {
      if (field.type === TextCollectionFieldType.Text && field.show_diff) {
        field.original_text = _.get(
          props,
          `params.hypothesis.annotations[${field.field_id}].response[0]`,
        );
      }
      return field;
    });
  }, [props.params.fields, props.loggedUserId]);

  const useCodeSidebar = React.useMemo(
    () =>
      Object.values(flattenFields(sortedFields)).some(
        field => field.type === TextCollectionFieldType.Code,
      ),
    [sortedFields],
  );

  const ocrTextFragments =
    _.size(textFragments) > 0 ? textFragments : props.params.ocrTextFragments ?? textFragments;

  const [state, dispatch] = useTextCollection({
    fields: sortedFields,
    annotations: props.response.annotations || {},
    readOnly: !!props.readOnly,
    reviewMode: !!props.reviewMode,
  });

  // Keep track of expanded FieldSets and review status for review mode.
  const [fieldSetReviews, setFieldSetReviews] = React.useState<
    Record<string, Record<number, FieldSetReview>>
  >(() => {
    if (!props.localBackupKey || props.readOnly) {
      return {};
    }
    const backup = getBackup(props.localBackupKey);
    if (!backup) {
      return {};
    }
    return backup.fieldSetReviews;
  });

  const [completedAttachments, setCompletedAttachments] = React.useState<boolean[]>([]);
  const [blockTasking, setBlockTasking] = React.useState<boolean>(false);

  React.useEffect(() => {
    setCompletedAttachments(new Array(attachments.length).fill(false));
  }, [attachments]);

  const activeForm = getActiveForm(sortedFields, state.breadcrumbs);

  React.useEffect(() => {
    const videoAttachmentExistsAndNotCompleted = _.some(
      attachments,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (attachment, i) =>
        _.includes(
          [TextCollectionAttachmentType.Video, TextCollectionAttachmentType.VideoComparison],
          attachment.type,
        ) && !completedAttachments[i],
    );

    const shouldBlockVideoTasking = !props.projectConfig
      ? false
      : !props.projectConfig?.videoAttachmentConfig?.disableBlockTaskingUntilVideoPlayedInFull &&
        videoAttachmentExistsAndNotCompleted;

    const shouldBlockTasking =
      shouldBlockVideoTasking ||
      _.some(
        attachments,
        (attachment, i) =>
          attachment.blocking &&
          !completedAttachments[i] &&
          isAttachmentRelevant(attachment, activeForm),
      );
    if (props.onSubmitDisabled) {
      props.onSubmitDisabled(shouldBlockTasking);
    }
    setBlockTasking(shouldBlockTasking);
  }, [activeForm, attachments, completedAttachments]);

  const [bboxFieldToEdit, setBboxFieldToEdit] = React.useState<string | null>(null);

  const maybeBoundingBox = bboxFieldToEdit
    ? getCurrentAnnotations(state.annotationsRef.current, state.breadcrumbs).annotations[
        bboxFieldToEdit
      ]
    : undefined;
  const boundingBox: BoundingBox | undefined =
    maybeBoundingBox?.type === TextCollectionFieldType.BoundingBox
      ? maybeBoundingBox.response[0]
      : undefined;

  const backupRef = React.useRef<TextCollectionResponse | null>(null);

  // Keep parent component updated.
  React.useEffect(() => {
    if (!props.readOnly) {
      const response = {
        annotations: state.annotations,
      };
      props.setResponse(response);
      backupRef.current = response;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.annotations, backupRef]);

  // Auto save local backups.
  React.useEffect(() => {
    const timeout = setInterval(() => {
      if (props.readOnly || !props.localBackupKey || !backupRef.current) {
        return;
      }
      saveBackup(props.localBackupKey, backupRef.current, fieldSetReviews);
    }, 3000);
    return () => {
      clearInterval(timeout);
    };
  }, [props.readOnly, props.localBackupKey, backupRef, fieldSetReviews]);

  // Auto save server-side backups.
  React.useEffect(() => {
    const timeout = setInterval(() => {
      const backupResponse = backupRef.current;
      if (props.readOnly || !props.assignmentId || !backupResponse) {
        return;
      }
      try {
        saveServerCheckpoint(props.assignmentId, backupResponse);
      } catch {
        /* do nothing */
      }
    }, 60000);
    return () => {
      clearInterval(timeout);
    };
  }, [props.assignmentId, props.readOnly, backupRef]);

  // Bind updates for the current selected FieldSet in editor mode.
  const setAnnotations = React.useCallback(
    (
      annotationsOrCallback: TextCollectionAnnotationsOrCallback,
      breadcrumbs?: Breadcrumb[],
    ): void => {
      dispatch({
        type: 'SET-ANNOTATIONS-TEMPORARY',
        payload: {
          annotations: getUpdateNextAnnotations(
            state.annotationsRef.current,
            breadcrumbs || state.breadcrumbs,
            _.isFunction(annotationsOrCallback)
              ? annotationsOrCallback(
                  getCurrentAnnotations(
                    state.annotationsRef.current,
                    breadcrumbs || state.breadcrumbs,
                  ).annotations,
                )
              : annotationsOrCallback,
          ),
        },
      });
    },
    [state.annotationsRef, state.breadcrumbs, dispatch],
  );
  const setAnnotationsTopLevel = React.useCallback(
    (annotationsOrCallback: TextCollectionAnnotationsOrCallback): void => {
      dispatch({
        type: 'SET-ANNOTATIONS-TEMPORARY',
        payload: {
          annotations: getUpdateNextAnnotations(
            state.annotationsRef.current,
            [],
            _.isFunction(annotationsOrCallback)
              ? annotationsOrCallback(
                  getCurrentAnnotations(state.annotationsRef.current, []).annotations,
                )
              : annotationsOrCallback,
          ),
        },
      });
    },
    [state.annotationsRef, dispatch],
  );

  const setBoundingBox = React.useCallback(
    (boundingBox: BoundingBox): void => {
      const currentAnnotations = getCurrentAnnotations(
        state.annotationsRef.current,
        state.breadcrumbs,
      ).annotations;
      const editedAnnotation = _.assign(currentAnnotations[bboxFieldToEdit!], {
        response: [boundingBox],
      });
      dispatch({
        type: 'SET-ANNOTATIONS-TEMPORARY',
        payload: {
          annotations: getUpdateNextAnnotations(
            state.annotationsRef.current,
            state.breadcrumbs,
            _.assign(
              getCurrentAnnotations(state.annotationsRef.current, state.breadcrumbs).annotations,
              {
                ...currentAnnotations,
                bboxFieldToEdit: editedAnnotation,
              },
            ),
          ),
        },
      });
    },
    [bboxFieldToEdit, state.annotationsRef, state.breadcrumbs, dispatch],
  );

  // Bind deep copy auto complete for the current selected FieldSet in editor mode.
  const autoComplete = React.useCallback(
    (query: AutoCompleteQuery): Record<string, TextCollectionAnnotation>[] => {
      return getAutoCompleteFieldSetResponses(state.annotations, state.breadcrumbs, query);
    },
    [state.annotations, state.breadcrumbs],
  );

  const setBreadcrumbs = React.useCallback(
    (breadcrumbs: Breadcrumb[], previousBreadcrumbs?: Breadcrumb[]) => {
      setBboxFieldToEdit(null);
      dispatch({
        type: 'SET-BREADCRUMBS',
        payload: {
          breadcrumbs,
          previousBreadcrumbs,
        },
      });
    },
    [dispatch, setBboxFieldToEdit],
  );
  const deleteAnnotations = React.useCallback(
    (breadcrumbs: Breadcrumb[]): void => {
      dispatch({
        type: 'SET-ANNOTATIONS-TEMPORARY',
        payload: {
          annotations: getDeleteNextAnnotations(state.annotationsRef.current, breadcrumbs),
        },
      });
    },
    [state.annotationsRef, dispatch],
  );

  // Navigate to the parent FieldSet in editor mode.
  const onRequestBack = (): void => {
    setBreadcrumbs(state.breadcrumbs.slice(0, state.breadcrumbs.length - 1));
  };

  // Switch to editor mode from review mode.
  const onRequestEdit = React.useCallback(
    (breadcrumbs: Breadcrumb[]): void => {
      setBreadcrumbs(breadcrumbs);
      dispatch({
        type: 'REVIEW-EDIT',
        payload: {
          breadcrumbs,
        },
      });
    },
    [setBreadcrumbs, dispatch],
  );

  // Delete the selected FieldSet and navigate to the parent FieldSet in editor mode.
  const onRequestDelete = () => {
    onRequestBack();
    // remove previous state on delete to prevent highlighting a previously visited node
    setBreadcrumbs(state.breadcrumbs.slice(0, Math.max(0, state.breadcrumbs.length - 1)) || [], []);
    dispatch({
      type: 'SET-ANNOTATIONS-TEMPORARY',
      payload: {
        annotations: getDeleteNextAnnotations(state.annotations, state.breadcrumbs),
      },
    });
  };

  // Reset response if changed (i.e. from speed audit)
  React.useEffect(() => {
    if (props.readOnly) {
      setAnnotationsTopLevel(props.response?.annotations || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.response]);

  const rankingOrderOptions = React.useMemo(() => {
    // TODO: Maybe extract the options from attachments somehow
    return attachments as TextCollectionRankingOrderOption[];
  }, [attachments]);

  const openFormsByAction: Partial<Record<TextCollectionFormAction, TextCollectionFormParams[]>> =
    React.useMemo(() => {
      return _.groupBy(
        (props.forms || []).filter(form => !!props.openForms?.[form.form_id]),
        'action',
      );
    }, [props.forms, props.openForms]);

  // Mark reviewed status for any Field or FieldSet in review mode.
  const setFieldSetReview = React.useCallback(
    (breadcrumbs: Breadcrumb[], reviewed: boolean): void => {
      const nextFieldSetReviews = getUpdateNextReviews(fieldSetReviews, breadcrumbs, reviewed);
      setFieldSetReviews(nextFieldSetReviews);
    },
    [setFieldSetReviews, fieldSetReviews],
  );

  const disableReviewMode = !!activeForm;
  const showTaskMode = state.currentMode === 'taskMode' || disableReviewMode;
  const showReviewMode = state.currentMode === 'reviewMode' && !disableReviewMode;

  const workflowHook = useWorkflowHook();
  const canUseGuidedWorkflow =
    props.assignment?.featureFlags?.canUseGuidedWorkflowBar &&
    props.assignment?.taskerWorkflowDefinition;

  const styles = useTextCollectionStyles();
  // TODO: make sensitive content warning more generalizable beyond sexual/minors

  const showSensitiveContentWarning =
    _.get(props.params?.metadata?.prediction, 'sexual/minors', 0.0) >= 0.7 ||
    _.get(props.params?.metadata?.prediction, 'violence/graphic', 0.0) >= 0.7 ||
    _.get(props.params?.metadata?.prediction, 'sexual/illegal', 0.0) >= 0.7;

  const randomizeReviewOptions = useUnleashFeatureFlag(
    'fair-randomize-review-options',
    { userId: props.loggedUserId },
    false,
  );

  const [fairHackInitialization] = useRLHFHackStore(state => [state.setInitialized]);
  React.useEffect(() => {
    fairHackInitialization(
      forceUseRlhfHackStore,
      props.params,
      state.annotations,
      setAnnotations,
      props.assignmentId,
      randomizeReviewOptions,
    );
  }, [props.assignment?.reviewLevel, randomizeReviewOptions]);
  return (
    <DocumentProvider
      attachments={attachments.length}
      ocrTextFragments={ocrTextFragments}
      keywords={KEYWORDS}
      annotationsRef={state.annotationsRef}
      fields={sortedFields}
    >
      <ChatbotRankingProvider
        useChatbotV2={useRLHFColumn}
        useFairStreaming={useFairStreaming}
        lintErrors={props.lintErrors}
        assignmentId={props.assignment?.assignmentId}
        isStudio={props.isScaleStudio}
        projectId={props.project ?? ''}
        useEditPrompt={props.projectConfig?.textCollectionConfig?.allowReviewerToEditRLHFPrompt}
        allowPastingInRLHFEditResponse={
          props.projectConfig?.textCollectionConfig?.allowPastingInRLHFEditResponse
        }
        forceEditCompletionLevenshteinThreshold={
          props.projectConfig?.textCollectionConfig?.forceEditCompletionLevenshteinThreshold
        }
        metadata={props.params.metadata}
        perTurnCheck={props.perTurnCheck}
        useAutoCritique={props.projectConfig?.textCollectionConfig?.autoCritique}
      >
        <LLMHelperProvider projectId={props.project ?? ''}>
          <SuggestionsProvider suggestionSources={props.params.suggestionSources}>
            <div className={styles.root}>
              <div
                className={classNames(styles.canvas, {
                  [styles.canvasWithSidebar]:
                    !props.hideTextCollectionForm ||
                    (showReviewMode &&
                      !(!showTaskMode || (mobile && !props.showSidebarResponsive))),
                  [styles.crabHackCanvas]: useCrabHack,
                  [styles.splitScreenCanvas]: useSplitScreen,
                  [styles.fullWidthSidebarCanvas]: props.fullWidthSidebar,
                })}
              >
                {canUseGuidedWorkflow && props.assignment?.taskerWorkflowDefinition && (
                  <GuidedWorkflowBar
                    workflowDefinition={props.assignment.taskerWorkflowDefinition}
                    workflowHook={workflowHook}
                    annotations={[]}
                  />
                )}
                {!props.fullWidthSidebar && (
                  <AttachmentCarousel
                    attachments={attachments}
                    activeForm={activeForm}
                    editBoundingBoxMode={!!bboxFieldToEdit}
                    boundingBoxField={boundingBox}
                    updateBoundingBoxField={setBoundingBox}
                    loggedUserId={props.loggedUserId}
                    useProtectedText={props.useProtectedText}
                    project={props.project}
                    completedAttachments={completedAttachments}
                    updateCompletedAttachments={setCompletedAttachments}
                    projectConfig={props.projectConfig}
                    isAuditMode={props.isAuditMode}
                    forceStacked={props.params.force_stacked}
                    stickyAttachments={_.get(props.params, 'sticky_attachments', [])}
                    showSensitiveContentWarning={showSensitiveContentWarning}
                    handleSkip={props.handleSkip}
                    startSelfDestructSequence={props.startSelfDestructSequence}
                    recordLinkClickingInTextAttachment={
                      !props.isAuditMode &&
                      props.onSubmitDisabled &&
                      (
                        props.projectConfig?.textCollectionConfig?.innerTextLinksToRecordClicks ??
                        []
                      ).length > 0
                        ? {
                            innerTextKeywords:
                              props.projectConfig!.textCollectionConfig!
                                .innerTextLinksToRecordClicks!,
                            onSubmitDisabled: props.onSubmitDisabled!,
                          }
                        : undefined
                    }
                    liveInstructions={props.params.liveInstructionsByAttachmentIndex}
                    overrideImageSize={
                      props.projectConfig?.textCollectionConfig?.useRawImageSize &&
                      props.params.image_height &&
                      props.params.image_width
                        ? {
                            height: props.params.image_height,
                            width: props.params.image_width,
                          }
                        : undefined
                    }
                    isCustomer={props.isCustomer}
                    reviewLevel={props.assignment?.reviewLevel}
                    isReview={props.isReview}
                    assignmentId={props.assignment?.assignmentId}
                    taskCustomer={props.taskCustomer}
                    subtaskMetadata={props.params.metadata}
                    setAnnotations={setAnnotations}
                    prefillPrompt={_.get(props.params, 'prefillPrompt', undefined)}
                    prefillRefText={_.get(props.params, 'prefillRefText', undefined)}
                  />
                )}
              </div>
              {/* TODO: put this flag check and the sidebarHidden conditions in the same place */}
              {!props.hideTextCollectionForm && (
                <div
                  className={classNames(styles.sidebar, {
                    [styles.sidebarHidden]:
                      !showTaskMode || (mobile && !props.showSidebarResponsive),
                    [styles.codeSidebar]: useCodeSidebar,
                    [styles.crabHackSidebar]: useCrabHack,
                    [styles.rlhfSidebar]: useRLHFColumn,
                    [styles.splitScreenSidebar]: useSplitScreen,
                    [styles.fullWidthSidebar]: props.fullWidthSidebar,
                  })}
                >
                  <div className={styles.form}>
                    <TextCollectionForm
                      isFair={_.get(props.params, 'isFair', false)}
                      rlhfParams={
                        _.get(props.params, 'chatApiEndpoint')
                          ? {
                              chatApiEndpoint: _.get(props.params, 'chatApiEndpoint', ''),
                              chatApiAdditionalParams: _.get(
                                props.params,
                                'chatApiAdditionalParams',
                                {},
                              ),
                              numberOfInteractions: _.get(props.params, 'numberOfInteractions', 0),
                              numberOfCompletions:
                                _.get(props.params, 'chatApiEndpoints')?.length ||
                                _.get(props.params, 'numberOfCompletions') ||
                                1,
                              perConversationFieldIds: _.get(
                                props.params,
                                'perConversationFieldIds',
                                [],
                              ),
                              perTurnFieldIds: _.get(props.params, 'perTurnFieldIds', []),
                              setCanSubmitRlhf: props.setCanSubmitRlhf,
                              editBestCompletion: _.get(props.params, 'editBestCompletion', false),
                              useEditedResponseInHistory: _.get(
                                props.params,
                                'useEditedResponseInHistory',
                                false,
                              ),
                              chatApiMultiEndpoints: _.get(
                                props.params,
                                'chatApiMultiEndpoints',
                                [],
                              ),
                              chatApiEndpoints: _.get(props.params, 'chatApiEndpoints', undefined),
                              enableMultiIterationsUntilPerfectAnswer: _.get(
                                props.params,
                                'enableMultiIterationsUntilPerfectAnswer',
                                false,
                              ),
                              maxIterationForPerfectAnswer: _.get(
                                props.params,
                                'maxIterationForPerfectAnswer',
                                0,
                              ),
                              rlhfBenchmarkType: _.get(props.params, 'rlhfBenchmarkType'),
                              isFair: _.get(props.params, 'isFair', false),
                              autoSpec: _.get(props.params, 'autoSpec', false),
                              autoCritique: _.get(props.params, 'autoCritique', false),
                              chatBoxTexts: _.get(props.params, 'chatBoxTexts', [
                                'Type your prompt',
                              ]),
                              flamingoPipelineVersion: _.get(
                                props.params,
                                'flamingoPipelineVersion',
                              ),
                              rangedNumberOfInteractions: _.get(
                                props.params,
                                'rangedNumberOfInteractions',
                              ),
                              maxRankingTies: _.get(props.params, 'maxRankingTies'),
                              showMarkdownViewForEditCompletion: _.get(
                                props.params,
                                'showMarkdownViewForEditCompletion',
                                false,
                              ),
                            }
                          : undefined
                      }
                      autoSpec={_.get(props.params, 'autoSpec', false)}
                      autoCritique={
                        _.get(props.params, 'autoCritique', false) ||
                        props.projectConfig?.textCollectionConfig?.autoCritique
                      }
                      aiAssistant={_.get(props.params, 'aiAssistant', false)}
                      setCanSubmitAiAssistant={props.setCanSubmitAiAssistant}
                      agreementScores={props.agreementScores}
                      title={props.params.title}
                      annotationsRef={state.annotationsRef}
                      deleteAnnotations={deleteAnnotations}
                      description={props.params.description}
                      breadcrumbs={state.breadcrumbs}
                      previousBreadcrumbs={state.previousBreadcrumbs}
                      setBreadcrumbs={setBreadcrumbs}
                      disabled={props.readOnly}
                      blocked={blockTasking}
                      fields={sortedFields}
                      decisionTreeFields={props.decisionTreeFields}
                      annotations={state.annotations}
                      setAnnotations={setAnnotations}
                      onRequestBack={onRequestBack}
                      onRequestDelete={onRequestDelete}
                      autoComplete={autoComplete}
                      readOnly={!!props.readOnly}
                      reviewMode={!!props.reviewMode && !disableReviewMode}
                      reviewLevel={props.assignment?.reviewLevel}
                      quickAddFieldSets={QUICK_ADD_FIELD_SETS}
                      dispatch={dispatch}
                      readOnlyFieldIds={props.params.read_only_field_ids}
                      blurredFieldIds={props.params.blurred_field_ids}
                      bboxFieldToEdit={bboxFieldToEdit}
                      setBboxFieldToEdit={setBboxFieldToEdit}
                      attachment={attachments[0]}
                      inlineAttachments={props.inlineAttachments}
                      completedAttachments={completedAttachments}
                      updateCompletedAttachments={setCompletedAttachments}
                      lintErrors={props.lintErrors}
                      projectId={props.project}
                      captcha={props.captcha}
                      captchaUserAnswer={props.captchaUserAnswer}
                      setCaptchaUserAnswer={props.setCaptchaUserAnswer}
                      setSubmitCodeError={props.setSubmitCodeError}
                      useSimpleCodeField={props.useSimpleCodeField}
                      onFormFieldNavigate={props.onFormFieldNavigate}
                      isScaleStudio={props.isScaleStudio}
                      assignmentId={props.assignmentId}
                      loggedUserId={props.loggedUserId}
                      textCollectionConfig={props.projectConfig?.textCollectionConfig}
                      isAudit={props.isAuditMode || props.assignment?.isAudit}
                      rankingOrderOptions={rankingOrderOptions}
                      useCrabHack={useCrabHack}
                      numLeftColumnFields={numLeftColumnFields}
                      useSplitScreen={useSplitScreen}
                      isReview={props.isReview}
                      isCustomer={props.isCustomer}
                      logBehaviourActions={props.logBehaviourActions}
                      perTurnCheck={props.perTurnCheck}
                      isTestQuestion={props.isTestQuestion}
                      chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
                      readOnlyMessage={props.readOnlyMessage}
                      templateVariables={props.params.templateVariables}
                    />
                  </div>
                </div>
              )}
              <div
                className={classNames(styles.sidebar, {
                  [styles.sidebarHidden]: !showTaskMode || (mobile && !props.showSidebarResponsive),
                })}
                style={{ display: showReviewMode ? 'block' : 'none' }}
              >
                <div className={styles.review}>
                  <AnnotationTree
                    fields={sortedFields}
                    description={props.params.description}
                    forms={props.params.forms}
                    annotations={state.annotations}
                    onRequestEdit={onRequestEdit}
                    requestShowForm={props.requestShowForm}
                    disabled={props.readOnly}
                    expanded={state.expanded}
                    expandAllNodes={state.expandAllNodes}
                    fieldSetReviews={fieldSetReviews}
                    setFieldSetReview={setFieldSetReview}
                    dispatch={dispatch}
                    attachment={attachments[0]}
                  />
                  {!props.readOnly && <ReviewFooter toast={state.toast} />}
                </div>
              </div>

              {openFormsByAction[TextCollectionFormAction.Submit]?.map((form, index) => {
                const formParams = _.find(
                  props.params.forms || [],
                  f => f.form_id === form.form_id,
                );
                if (formParams) {
                  return (
                    <TextCollectionModalForm
                      open={true}
                      key={index}
                      formParams={formParams}
                      annotations={state.annotations}
                      annotationsRef={state.annotationsRef}
                      setAnnotations={setAnnotationsTopLevel}
                      dispatch={dispatch}
                      onSubmit={props.requestSubmit}
                      onClose={props.requestCloseForm}
                      bboxFieldToEdit={bboxFieldToEdit}
                      setBboxFieldToEdit={setBboxFieldToEdit}
                      attachment={attachments[0]}
                      readOnly={!!props.readOnly}
                    />
                  );
                }
                return null;
              })}
            </div>
          </SuggestionsProvider>
        </LLMHelperProvider>
      </ChatbotRankingProvider>
    </DocumentProvider>
  );
};

const useTextCollectionStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
  },
  canvas: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      width: '100%',
    },
  },
  canvasWithSidebar: {
    width: 'calc(100% - 460px)',
  },
  fullWidthSidebarCanvas: {
    flex: 0,
  },
  fullWidthSidebar: {
    width: '100% !important',
    borderLeft: 'none !important',
  },
  crabHackCanvas: {
    width: '200px',
  },
  splitScreenCanvas: {
    flex: 0,
    minWidth: '51%',
  },
  splitScreenSidebar: {
    minWidth: '50%',
  },
  sidebar: {
    position: 'relative',
    height: '100%',
    width: 460,
    overflow: 'hidden',
    borderLeft: '1px solid #E6E6E6',
    background: 'white',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      width: '100%',
      position: 'absolute',
      border: 'none',
      display: 'block',
    },
  },
  codeSidebar: {
    width: '66%',
  },
  crabHackSidebar: {
    width: '66%',
  },
  rlhfSidebar: {
    width: '50%',
  },
  sidebarHidden: {
    display: 'none',
  },
  description: {
    fontWeight: 500,
    color: '#000000',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '16px',
    marginTop: '8px',
  },
  form: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  review: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

const getActiveForm = (fields: TextCollectionField[], breadcrumbs: Breadcrumb[]): string => {
  if (breadcrumbs.length !== 1 || !isFormFields(fields)) {
    return '';
  }
  return breadcrumbs[0].id;
};
